/**
 * GPS connectors (API 0440, Geotab 0442): the payload shapes and their parsers.
 *
 * The API returns gps_connections rows as SELECTed, so they arrive snake_case;
 * every read here takes the camelCase spelling too, in case the API grows a
 * transform. Absent stays absent: a null `vehicles_matched` (no sync yet) is
 * null here and an em dash on screen, never 0.
 */

export const GPS_PROVIDERS = ['motive', 'samsara', 'geotab'] as const;
export type GpsProvider = (typeof GPS_PROVIDERS)[number];

export const GPS_PROVIDER_LABELS: Record<GpsProvider, string> = {
  motive: 'Motive',
  samsara: 'Samsara',
  geotab: 'Geotab',
};

/**
 * Geotab logs in with username + password + database, not an API key. The
 * password is the secret and no read returns any part of it — key_last4 is
 * null for Geotab, and the card shows the username and database instead.
 */
export function usesLogin(provider: GpsProvider): provider is 'geotab' {
  return provider === 'geotab';
}

/** The API's SAMSARA_REGIONS, in its order. A key works only in its own region. */
export const SAMSARA_REGIONS = [
  { value: 'us', label: 'US' },
  { value: 'ca', label: 'Canada' },
  { value: 'eu', label: 'EU' },
] as const;

/** gps_connections_status_chk: active | error | disconnected. */
export type GpsStatus = 'active' | 'error' | 'disconnected';

export interface GpsConnection {
  provider: GpsProvider;
  keyLast4: string | null;
  region: string | null;
  /** Geotab only. */
  accountUsername: string | null;
  accountDatabase: string | null;
  status: GpsStatus | null;
  lastSyncAt: string | null;
  lastError: string | null;
  vehiclesMatched: number | null;
}

export interface GpsMatchedUnit {
  equipmentId: string;
  unitNumber: string | null;
  name: string | null;
  providerVehicleId: string;
  providerVehicleName: string | null;
  matchedOn: string | null;
}

export interface GpsUnmatchedVehicle {
  providerVehicleId: string;
  name: string | null;
  vin: string | null;
  plate: string | null;
  reason: string | null;
}

export interface GpsMissingIdentifier {
  equipmentId: string;
  unitNumber: string | null;
  name: string | null;
}

export interface GpsMatchReport {
  matched: GpsMatchedUnit[];
  unmatchedProviderVehicles: GpsUnmatchedVehicle[];
  missingIdentifiers: GpsMissingIdentifier[];
}

type Row = Record<string, unknown>;

function pick(row: Row, camel: string, snake: string): unknown {
  return row[camel] ?? row[snake];
}

function str(v: unknown): string | null {
  return typeof v === 'string' && v !== '' ? v : null;
}

function num(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function isProvider(v: unknown): v is GpsProvider {
  return typeof v === 'string' && (GPS_PROVIDERS as readonly string[]).includes(v);
}

/** An unrecognised status is null — the card claims nothing about it. */
function status(v: unknown): GpsStatus | null {
  return v === 'active' || v === 'error' || v === 'disconnected' ? v : null;
}

export function parseGpsConnections(payload: unknown): GpsConnection[] {
  if (!Array.isArray(payload)) return [];
  return payload
    .filter((r): r is Row => !!r && typeof r === 'object' && isProvider((r as Row).provider))
    .map((r) => ({
      provider: r.provider as GpsProvider,
      keyLast4: str(pick(r, 'keyLast4', 'key_last4')),
      region: str(r.region),
      accountUsername: str(pick(r, 'accountUsername', 'account_username')),
      accountDatabase: str(pick(r, 'accountDatabase', 'account_database')),
      status: status(r.status),
      lastSyncAt: str(pick(r, 'lastSyncAt', 'last_sync_at')),
      lastError: str(pick(r, 'lastError', 'last_error')),
      vehiclesMatched: num(pick(r, 'vehiclesMatched', 'vehicles_matched')),
    }));
}

/**
 * Connected means the API holds a key: a row that is not `disconnected`. An
 * `error` row is still connected — its key is stored and Sync now retries it.
 */
export function isConnected(c: GpsConnection | undefined): c is GpsConnection {
  return !!c && c.status !== 'disconnected';
}

function rows(v: unknown): Row[] {
  return Array.isArray(v) ? v.filter((r): r is Row => !!r && typeof r === 'object') : [];
}

export function parseGpsMatchReport(payload: unknown): GpsMatchReport | null {
  if (!payload || typeof payload !== 'object') return null;
  const p = payload as Row;
  return {
    matched: rows(p.matched).map((r) => ({
      equipmentId: String(pick(r, 'equipmentId', 'equipment_id') ?? ''),
      unitNumber: str(pick(r, 'unitNumber', 'unit_number')),
      name: str(r.name),
      providerVehicleId: String(pick(r, 'providerVehicleId', 'provider_vehicle_id') ?? ''),
      providerVehicleName: str(pick(r, 'providerVehicleName', 'provider_vehicle_name')),
      matchedOn: str(pick(r, 'matchedOn', 'matched_on')),
    })),
    unmatchedProviderVehicles: rows(pick(p, 'unmatchedProviderVehicles', 'unmatched_provider_vehicles')).map((r) => ({
      providerVehicleId: String(pick(r, 'providerVehicleId', 'provider_vehicle_id') ?? ''),
      name: str(r.name),
      vin: str(r.vin),
      plate: str(r.plate),
      reason: str(r.reason),
    })),
    missingIdentifiers: rows(pick(p, 'fieldiqVehiclesMissingIdentifiers', 'fieldiq_vehicles_missing_identifiers')).map((r) => ({
      equipmentId: String(pick(r, 'equipmentId', 'equipment_id') ?? ''),
      unitNumber: str(pick(r, 'unitNumber', 'unit_number')),
      name: str(r.name),
    })),
  };
}

/** A FieldIQ unit's label: unit number, then name, then an em dash. */
export function unitLabel(u: { unitNumber: string | null; name: string | null }): string {
  if (u.unitNumber && u.name) return `${u.unitNumber} · ${u.name}`;
  return u.unitNumber ?? u.name ?? '—';
}
